<script setup lang="ts">
import { ref, watch } from 'vue';
import FullCalendar from '@fullcalendar/vue3';
import listPlugin from '@fullcalendar/list';
import { useAttendanceStore } from '@/store';
import { storeToRefs } from 'pinia';

import { CalendarEventInterface, TherapyResultsInterface } from '@/types';

interface ArgumentInterface {
  event: CalendarEventInterface;
}

const AttendanceStore = useAttendanceStore();
const { therapyResults, start_at, end_at } = storeToRefs(AttendanceStore);
const primeCalendar = ref();

/**
 * Transform string into kebab-case
 *
 * Input example: IEP Meeting
 * Output example: iep-meeting
 *
 * @param str
 */
function convertToKebabCase(str: string) {
  if (!str) {
    return '';
  }

  return str
    .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
    .map((x) => x.toLowerCase())
    .join('-');
}

function getTherapyResultData() {
  const events: CalendarEventInterface[] = [];

  therapyResults.value.map((result: TherapyResultsInterface) => {
    events.push({
      title: result.therapy_result_type?.name,
      classNames: [
        result.therapy_result_type?.is_present ? 'attended' : 'no-show',
        convertToKebabCase(result.event.therapy_event_type),
      ],
      start: new Date(result.event.start_at),
      end: new Date(result.event.end_at),
      editable: false,
      extendedProps: {
        description: result.notes,
      },
    });
  });

  if (!primeCalendar.value) {
    return events;
  }

  const calendar = primeCalendar.value.getApi();
  calendar.removeAllEvents();
  calendar.addEventSource(events);
}

const calendarOptions = ref({
  plugins: [listPlugin],
  initialView: 'list',
  visibleRange: {
    start: start_at.value + 'T00:00:00',
    end: end_at.value + 'T23:59:59',
  },
  headerToolbar: false,
  listDayFormat: { month: 'numeric', day: 'numeric', year: 'numeric' },
  stickyHeaderDates: false,
  listDaySideFormat: false,
  height: '100%',
  eventBackgroundColor: '#ffffff',
  eventBorderColor: '#f1f3f3',
  eventTextColor: '#3B4242',
  eventMinHeight: 24,
  events: getTherapyResultData(),
  eventContent: function (arg: ArgumentInterface) {
    let title = document.createElement('span');
    title.innerHTML = arg.event.title;
    title.className = 'white-space-nowrap';

    let arrayOfDomNodes = [title];
    return { domNodes: arrayOfDomNodes };
  },
});

watch(therapyResults, () => {
  getTherapyResultData();
  calendarOptions.value.visibleRange = {
    start: start_at.value + 'T00:00:00',
    end: end_at.value + 'T23:59:59',
  };
});
</script>

<template>
  <div class="attendance-calendar bg-white mr-4 mb-4 overflow-hidden w-full">
    <div class="attendance-calendar-row flex">
      <FullCalendar ref="primeCalendar" :options="calendarOptions" class="flex-1 w-full border-0" />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/theme/extensions/calendar';

.attendance-calendar {
  .attendance-calendar-row:first-child {
    height: 22rem;

    @media (max-width: 960px) {
      flex-direction: column;
      height: 44rem;

      .fc {
        border-left: none;
        border-top: 2px solid $grayLight;
      }
    }
  }
  :deep(.fc .fc-list-day a.fc-list-day-text) {
    font-weight: normal !important;
    color: $grayDark;
    &::before {
      content: '';
    }
  }
  :deep(.fc-list-event-time) {
    font-weight: bold;
  }
  :deep(.fc-scroller) {
    right: 4px;
    overflow: auto !important;
  }
  :deep(.fc-scroller::-webkit-scrollbar) {
    height: 0.5rem;
  }
}
</style>
